/** The `health` block of a TCP, HTTP or IPsec share: where it stands, from the port check each host runs. */
export interface ShareHealthV3 {
    /** `HEALTHY`, `DEGRADED`, `FAILED_OVER`, `DOWN`, `MISCONFIGURED`, `PENDING` or `UNKNOWN`. Read it through {@link shareHealthState}. */
    state?: string;
    /** Every host of the share, the actives first. */
    hosts?: HostHealthV3[];
    /** When Ziti last confirmed this health. */
    checkedAt?: string;
}

/** One host of a share as Ziti sees it. Read it through {@link hostHealthState}. */
export interface HostHealthV3 {
    agentId: string;
    /** `ACTIVE` or `PASSIVE`. */
    role?: string;
    /** `SERVING`, `FAILING`, `NOT_BOUND`, `MISCONFIGURED` or `UNKNOWN`. */
    state?: string;
    /** The terminator precedence that decided the state: `required`, `default` or `failed`. */
    precedence?: string;
    /** How many Ziti terminators the host has for the share's service. */
    terminators?: number;
}

/** Any share that can carry a `health` block. The target names what a failing agent cannot reach. */
export interface ShareWithHealth {
    health?: ShareHealthV3 | null;
    targetAddress?: string;
    targetPort?: number;
}

export type ShareHealthState = 'healthy' | 'degraded' | 'failedOver' | 'down' | 'misconfigured' | 'pending' | 'unknown';

export type HostHealthState = 'serving' | 'failing' | 'notBound' | 'misconfigured' | 'unknown';

/** How a health state reads on screen, in the `.nf-badge` tones. */
export interface ShareHealthBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
}

/** A host's health as a host chip carries it. */
export interface HostHealthChip {
    state: HostHealthState;
    badge: ShareHealthBadge;
}

const SHARE_HEALTH_WIRE_STATES = new Map<string, ShareHealthState>([
    ['HEALTHY', 'healthy'],
    ['DEGRADED', 'degraded'],
    ['FAILED_OVER', 'failedOver'],
    ['DOWN', 'down'],
    ['MISCONFIGURED', 'misconfigured'],
    ['PENDING', 'pending'],
    ['UNKNOWN', 'unknown'],
]);

const SHARE_HEALTH_BADGES: Record<ShareHealthState, ShareHealthBadge> = {
    healthy: { tone: 'ok', label: 'Healthy' },
    degraded: { tone: 'warn', label: 'Degraded' },
    failedOver: { tone: 'warn', label: 'Failed Over' },
    down: { tone: 'err', label: 'Down' },
    misconfigured: { tone: 'err', label: 'Misconfigured' },
    pending: { tone: 'warn', label: 'Pending' },
    unknown: { tone: 'muted', label: 'Unknown' },
};

const HOST_HEALTH_WIRE_STATES = new Map<string, HostHealthState>([
    ['SERVING', 'serving'],
    ['FAILING', 'failing'],
    ['NOT_BOUND', 'notBound'],
    ['MISCONFIGURED', 'misconfigured'],
    ['UNKNOWN', 'unknown'],
]);

const HOST_HEALTH_BADGES: Record<HostHealthState, ShareHealthBadge> = {
    serving: { tone: 'ok', label: 'Serving' },
    failing: { tone: 'err', label: 'Failing' },
    notBound: { tone: 'muted', label: 'Not Bound' },
    misconfigured: { tone: 'err', label: 'Misconfigured' },
    unknown: { tone: 'muted', label: 'Unknown' },
};

/** The states the listing filter offers, from best to worst. */
export const SHARE_HEALTH_OPTIONS: ShareHealthState[] = [
    'healthy',
    'degraded',
    'failedOver',
    'down',
    'misconfigured',
    'pending',
    'unknown',
];

/** The share's health, or null where the share carries no `health` block, such as a create response. */
export function shareHealthState(share: ShareWithHealth | null | undefined): ShareHealthState | null {
    if (!share?.health) {
        return null;
    }
    return SHARE_HEALTH_WIRE_STATES.get((share.health.state ?? '').toUpperCase()) ?? 'unknown';
}

export function shareHealthBadge(state: ShareHealthState): ShareHealthBadge {
    return SHARE_HEALTH_BADGES[state] ?? SHARE_HEALTH_BADGES.unknown;
}

/** The wire spelling of a state, for the listing's `health` filter. */
export function shareHealthWireState(state: ShareHealthState): string {
    for (const [wire, known] of SHARE_HEALTH_WIRE_STATES) {
        if (known === state) {
            return wire;
        }
    }
    return '';
}

export function hostHealthState(host: HostHealthV3 | null | undefined): HostHealthState {
    return HOST_HEALTH_WIRE_STATES.get((host?.state ?? '').toUpperCase()) ?? 'unknown';
}

export function hostHealthBadge(state: HostHealthState): ShareHealthBadge {
    return HOST_HEALTH_BADGES[state] ?? HOST_HEALTH_BADGES.unknown;
}

/**
 * The health of the host `agentId`, or null where the block does not list that agent. The entry in `role` wins, and any
 * other entry for the agent stands in, because `role` is optional and lags a role change until the next check.
 */
export function hostHealthChip(
    health: ShareHealthV3 | null | undefined,
    agentId: string,
    role: 'active' | 'passive'
): HostHealthChip | null {
    const entries = (health?.hosts ?? []).filter((entry) => entry.agentId === agentId);
    const host = entries.find((entry) => (entry.role ?? '').toLowerCase() === role) ?? entries[0];
    if (!host) {
        return null;
    }
    const state = hostHealthState(host);
    return { state, badge: hostHealthBadge(state) };
}

/** What a share drawer's Health row draws: the badge, when Ziti confirmed it, and why a state needs a look. */
export interface ShareHealthView {
    state: ShareHealthState;
    badge: ShareHealthBadge;
    checkedAt: string;
    /** One line on a state that is not healthy, or '' where the badge says enough. */
    note: string;
    /** The note in runs, so a template can set the share target in monospace. Empty where the note is. */
    noteParts: ShareHealthNotePart[];
}

/** A run of a health note. `mono` marks the share's `host:port`. */
export interface ShareHealthNotePart {
    text: string;
    mono: boolean;
}

/** `address:port`, or the address alone without a port, or '' where the share names no target. */
export function shareTarget(share: { targetAddress?: string; targetPort?: number } | null | undefined): string {
    const address = share?.targetAddress ?? '';
    const port = share?.targetPort;
    return address && port ? `${address}:${port}` : address;
}

/** The Health row of a share drawer, or null where the share carries no `health` block. */
export function shareHealthView(share: ShareWithHealth | null | undefined): ShareHealthView | null {
    const state = shareHealthState(share);
    if (!state) {
        return null;
    }
    const note = shareHealthNote(state, share);
    return {
        state,
        badge: shareHealthBadge(state),
        checkedAt: share?.health?.checkedAt ?? '',
        note,
        noteParts: noteParts(note, shareTarget(share)),
    };
}

/**
 * Why a share is not healthy. Down, Degraded and Failed Over name the agents that do not serve and why: a failing
 * agent ran the port check and could not reach the target, and an agent that is not bound has no terminator.
 */
function shareHealthNote(state: ShareHealthState, share: ShareWithHealth | null | undefined): string {
    const hosts = (share?.health?.hosts ?? []).map((host) => hostHealthState(host));
    const failing = hosts.filter((host) => host === 'failing').length;
    const notBound = hosts.filter((host) => host === 'notBound').length;
    const target = shareTarget(share) || 'the target';
    const reason = notServingReason(failing, notBound, target);
    switch (state) {
        case 'down':
            if (failing > 0 && notBound === 0) {
                return `No agent can reach ${target}, so this share does not answer. Check that the target is up and that the agents can reach it.`;
            }
            if (notBound > 0 && failing === 0) {
                return 'No agent of this share is connected, so it does not answer. Check that the agents are online.';
            }
            if (reason) {
                return `${reason}, so this share does not answer.`;
            }
            return 'No agent serves this share, so it does not answer. An agent must be online and able to reach the target.';
        case 'degraded':
            return `An active agent serves this share. ${reason ? `${reason}.` : 'Another agent does not.'}`;
        case 'failedOver':
            return `No active agent serves this share, so a passive agent takes the traffic.${
                reason ? ` ${reason}.` : ''
            }`;
        case 'misconfigured':
            return 'An agent serves at a precedence its role does not ask for. If this does not clear, contact NetFoundry support.';
        case 'unknown':
            return 'Ziti did not answer in time for at least one agent, so the health of this share is not known.';
        default:
            return '';
    }
}

/** Such as "1 agent cannot reach db.local:5432, and 2 agents are not connected", or '' where no agent fails. */
function notServingReason(failing: number, notBound: number, target: string): string {
    const parts: string[] = [];
    if (failing > 0) {
        parts.push(`${agentCount(failing)} cannot reach ${target}`);
    }
    if (notBound > 0) {
        parts.push(`${agentCount(notBound)} ${notBound === 1 ? 'is' : 'are'} not connected`);
    }
    return parts.join(', and ');
}

/** Split `note` around each mention of `target`, which is the only machine value a note carries. */
function noteParts(note: string, target: string): ShareHealthNotePart[] {
    if (!note) {
        return [];
    }
    if (!target) {
        return [{ text: note, mono: false }];
    }
    const parts: ShareHealthNotePart[] = [];
    note.split(target).forEach((text, index) => {
        if (index > 0) {
            parts.push({ text: target, mono: true });
        }
        if (text) {
            parts.push({ text, mono: false });
        }
    });
    return parts;
}

function agentCount(count: number): string {
    return `${count} ${count === 1 ? 'agent' : 'agents'}`;
}
